import { z } from "zod";
import { isIsoCountryCode } from "@libs/utils/isoCountries";

/**
 * Facts about the buyer a read names (the listing design's §5): where the
 * fan is, and where their order goes once the sheet knows it. The server
 * picks the fan's listing for both and describes the purchase for it.
 */

/** Where a fan is, as far as Lissen knows, when it knows nothing: GB,
 *  today's market (the listing design's §5). */
export const UNKNOWN_FAN_COUNTRY = "GB";

/**
 * The fan country a read names: the first of `candidates`, most explicit
 * first, that is a country code (ISO 3166-1 alpha-2, and XK:
 * `isIsoCountryCode`), in capitals. A value that is none ("UK", "Worldwide",
 * "", a number) is unknown, never an error; with none known, GB.
 */
export function fanCountryOf(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const code = candidate.trim().toUpperCase();
    if (isIsoCountryCode(code)) return code;
  }
  return UNKNOWN_FAN_COUNTRY;
}

/**
 * The delivery country a read names: the buyer's address country, once the
 * sheet knows it (`deliverTo`), as a country code in capitals. Unlike the
 * fan country it has no default: a value that is no country code, or none,
 * is not known yet, and the fan country stands in for it.
 */
export function deliverToOf(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const code = value.trim().toUpperCase();
  return isIsoCountryCode(code) ? code : undefined;
}

/** A fan country or delivery country a tRPC read names: one that is not
 *  text, or too long to be a country, is unknown, never a refused request
 *  (`fanCountryOf` and `deliverToOf` read what is left). */
export const fanCountryInputSchema = z.string().max(40).optional().catch(undefined);

/** What a tRPC read that describes purchases takes of its fan: the app's
 *  access reads (`accesses.getById`, `listUserAccesses`,
 *  `listCurrentAccessesByArtistId`). */
export const fanReadInput = {
  country: fanCountryInputSchema,
  deliverTo: fanCountryInputSchema,
};

/** A fan as a read names them: where they are, and where their order goes
 *  once the sheet knows it. */
export type ReadFan = { country: string; deliverTo?: string };

/** The fan a read names: its fan country the `country` it names, else
 *  `fanCountry` (the API's own: a signed-in fan's plan country, else the
 *  device region the request's `country-code` carries), else GB, and its
 *  delivery country the `deliverTo` it names, if any. */
export function fanOf(input: { country?: unknown; deliverTo?: unknown } | undefined, fanCountry?: string): ReadFan {
  const deliverTo = deliverToOf(input?.deliverTo);
  return { country: fanCountryOf(input?.country, fanCountry), ...(deliverTo ? { deliverTo } : {}) };
}
